import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getResolutionsFilteringOptions,
  getClearBasicModel,
} from "../../common/EntitiesManagement";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function Proceeding() {
  const {
    proceedingModel,
    setProceedingModel,
    proceedingData,
    PROCEEDING_ENDPOINT,
    config,
    pageIndexProceeding,
    setPageIndexProceeding,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [proceedingDataFiltered, setProceedingDataFiltered] = useState(
    proceedingData?.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getResolutionsFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const operationalDataObj = proceedingData.Data.find(
        (operationalAreaObj) => operationalAreaObj.Id === Number(id)
      );
      setProceedingModel(operationalDataObj);
    }
  }, [id]);

  useEffect(() => {
    setProceedingDataFiltered(proceedingData.Data);
  }, [proceedingData]);

  const validateOperationResult = (result, isDelete) => {
    setProceedingModel(getClearBasicModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([PROCEEDING_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexProceeding !== 0 && proceedingModel.Data.length === 1) {
        setPageIndexProceeding(pageIndexProceeding - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${PROCEEDING_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${PROCEEDING_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${PROCEEDING_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !proceedingModel?.Name ||
    proceedingModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.code,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.proceedings}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={PROCEEDING_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="proceeding"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          validators={disableSaveChanges()}
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_proceeding}
          isEditForm
          onClickToSubmit={() => {
            handleUpdateRequest(proceedingModel);
            setShowEditModal(false);
            setProceedingModel(getClearBasicModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setProceedingModel(getClearBasicModel());
          }}
          Component={
            <AddOrEditBasicEntity
              customWidth="100%"
              nameMandatory
              codeMandatory
              model={proceedingModel}
              setModel={setProceedingModel}
              codeFieldText={localize.code}
            />
          }
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.proceedings}
            subtitle={localize.proceeding_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="proceeding_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                validators={disableSaveChanges()}
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_proceeding}
                onClickToSubmit={() => {
                  handleCreateRequest(proceedingModel);
                  setShowAddModal(false);
                  setProceedingModel(getClearBasicModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setProceedingModel(getClearBasicModel());
                }}
                Component={
                  <AddOrEditBasicEntity
                    customWidth="100%"
                    nameMandatory
                    codeMandatory
                    model={proceedingModel}
                    setModel={setProceedingModel}
                    codeFieldText={localize.code}
                  />
                }
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={proceedingModel.Name}
        entityType={localize.proceeding}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setProceedingModel(getClearBasicModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(proceedingModel.Id);
          setProceedingModel(getClearBasicModel());
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexProceeding}
            setPageIndex={setPageIndexProceeding}
            dataArray={proceedingData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          proceedingData.Data?.length !== 0
            ? proceedingDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setProceedingModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setProceedingModel(item);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <ValueOrNoDataLabel data={item.Code} />,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(6)]
        }
      />
    </div>
  );
}
